import { useCallback, useEffect, useRef, useState } from "react";
import { Button, Spinner } from "react-bootstrap";
import { createCornerEditor, extractDocument, scanDocument } from "scanic";

const OUTPUT_WIDTH = 900;
const OUTPUT_HEIGHT = Math.round(OUTPUT_WIDTH * 88 / 63);
const CARD_ASPECT_RATIO = 88 / 63;
const GUIDE = { width: 360, height: 480, left: 32, top: 34, right: 328, bottom: 446 };
const CORNER_KEYS = ["topLeft", "topRight", "bottomRight", "bottomLeft"];

const openCamera = async () => {
  if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
    const error = new Error("La camara web requiere HTTPS y un navegador compatible.");
    error.code = "UNSUPPORTED_CAMERA";
    throw error;
  }
  const attempts = [
    { video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } }, audio: false },
    { video: { facingMode: "environment" }, audio: false },
    { video: true, audio: false }
  ];
  let lastError;
  for (const constraints of attempts) {
    try {
      return await navigator.mediaDevices.getUserMedia(constraints);
    } catch (error) {
      lastError = error;
      if (error?.name === "NotAllowedError" || error?.name === "SecurityError") throw error;
    }
  }
  throw lastError || new Error("No se encontro una camara disponible.");
};

const cameraErrorMessage = error => {
  if (error?.code === "UNSUPPORTED_CAMERA") return error.message;
  if (error?.name === "NotAllowedError" || error?.name === "SecurityError") return "Permiso de camara bloqueado. Habilitalo en los ajustes del navegador.";
  if (error?.name === "NotFoundError" || error?.name === "DevicesNotFoundError") return "No encontramos una camara disponible.";
  if (error?.name === "NotReadableError" || error?.name === "TrackStartError") return "La camara esta siendo usada por otra aplicacion.";
  return "No pudimos abrir la camara. Puedes subir una foto desde el dispositivo.";
};

const canvasToFile = (canvas, filename) => new Promise((resolve, reject) => {
  canvas.toBlob(blob => {
    if (!blob) return reject(new Error("No se pudo preparar la carta."));
    resolve(new File([blob], filename, { type: "image/jpeg", lastModified: Date.now() }));
  }, "image/jpeg", 0.9);
});

const captureVideoFrame = video => {
  const sourceAspect = video.videoWidth / video.videoHeight;
  const viewportAspect = (video.clientWidth || video.videoWidth) / (video.clientHeight || video.videoHeight);
  let sourceX = 0;
  let sourceY = 0;
  let cropWidth = video.videoWidth;
  let cropHeight = video.videoHeight;

  if (sourceAspect > viewportAspect) {
    cropWidth = video.videoHeight * viewportAspect;
    sourceX = (video.videoWidth - cropWidth) / 2;
  } else if (sourceAspect < viewportAspect) {
    cropHeight = video.videoWidth / viewportAspect;
    sourceY = (video.videoHeight - cropHeight) / 2;
  }

  const maxDimension = 1600;
  const scale = Math.min(1, maxDimension / Math.max(cropWidth, cropHeight));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(cropWidth * scale));
  canvas.height = Math.max(1, Math.round(cropHeight * scale));
  canvas.getContext("2d", { alpha: false }).drawImage(
    video, sourceX, sourceY, cropWidth, cropHeight, 0, 0, canvas.width, canvas.height
  );
  return canvas;
};

const guideCorners = canvas => {
  const scale = Math.min(canvas.width / GUIDE.width, canvas.height / GUIDE.height);
  const offsetX = (canvas.width - GUIDE.width * scale) / 2;
  const offsetY = (canvas.height - GUIDE.height * scale) / 2;
  return {
    topLeft: { x: offsetX + GUIDE.left * scale, y: offsetY + GUIDE.top * scale },
    topRight: { x: offsetX + GUIDE.right * scale, y: offsetY + GUIDE.top * scale },
    bottomRight: { x: offsetX + GUIDE.right * scale, y: offsetY + GUIDE.bottom * scale },
    bottomLeft: { x: offsetX + GUIDE.left * scale, y: offsetY + GUIDE.bottom * scale }
  };
};

const distance = (a, b) => Math.hypot(b.x - a.x, b.y - a.y);

const polygonArea = corners => {
  const points = CORNER_KEYS.map(key => corners[key]);
  return Math.abs(points.reduce((sum, point, index) => {
    const next = points[(index + 1) % points.length];
    return sum + point.x * next.y - next.x * point.y;
  }, 0) / 2);
};

const reliableDetection = (result, source) => {
  const corners = result?.corners;
  if (!result?.success || !corners || CORNER_KEYS.some(key => !Number.isFinite(corners[key]?.x))) return false;
  const width = (distance(corners.topLeft, corners.topRight) + distance(corners.bottomLeft, corners.bottomRight)) / 2;
  const height = (distance(corners.topLeft, corners.bottomLeft) + distance(corners.topRight, corners.bottomRight)) / 2;
  const aspect = Math.max(width, height) / Math.max(1, Math.min(width, height));
  const coverage = polygonArea(corners) / (source.width * source.height);
  const centerX = CORNER_KEYS.reduce((sum, key) => sum + corners[key].x, 0) / 4 / source.width;
  const centerY = CORNER_KEYS.reduce((sum, key) => sum + corners[key].y, 0) / 4 / source.height;
  return aspect >= 1.18 && aspect <= 1.72
    && Math.abs(aspect - CARD_ASPECT_RATIO) <= 0.24
    && coverage >= 0.18 && coverage <= 0.94
    && Math.abs(centerX - 0.5) <= 0.2 && Math.abs(centerY - 0.5) <= 0.2;
};

function LiveCardScanner({ side = "front", onCapture, onCancel }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const sourceCanvasRef = useRef(null);
  const editorHostRef = useRef(null);
  const editorRef = useRef(null);
  const [stage, setStage] = useState("camera");
  const [detectedCorners, setDetectedCorners] = useState(null);
  const [cropQuality, setCropQuality] = useState(null);
  const [status, setStatus] = useState("Abriendo camara...");
  const [error, setError] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach(track => track.stop());
    streamRef.current = null;
  }, []);

  const startCamera = useCallback(async () => {
    setError("");
    setStatus(side === "back"
      ? "Da vuelta la carta y captura el reverso, o puedes omitirlo."
      : "Alinea la carta completa y toma la foto.");
    try {
      const stream = await openCamera();
      streamRef.current = stream;
      if (!videoRef.current) return;
      videoRef.current.srcObject = stream;
      await videoRef.current.play();
    } catch (cameraError) {
      stopCamera();
      setError(cameraErrorMessage(cameraError));
    }
  }, [side, stopCamera]);

  useEffect(() => {
    startCamera();
    return () => {
      editorRef.current?.destroy();
      editorRef.current = null;
      stopCamera();
    };
  }, [startCamera, stopCamera]);

  useEffect(() => {
    if (stage !== "camera" || !videoRef.current || !streamRef.current) return;
    videoRef.current.srcObject = streamRef.current;
    videoRef.current.play().catch(() => {});
  }, [stage]);

  const finishCrop = useCallback(async corners => {
    if (!sourceCanvasRef.current) return;
    setIsProcessing(true);
    setError("");
    try {
      const result = await extractDocument(sourceCanvasRef.current, corners, { output: "canvas" });
      if (!result.success || !(result.output instanceof HTMLCanvasElement)) {
        throw new Error(result.message || "No pudimos corregir la perspectiva.");
      }
      const normalized = document.createElement("canvas");
      normalized.width = OUTPUT_WIDTH;
      normalized.height = OUTPUT_HEIGHT;
      const context = normalized.getContext("2d", { alpha: false });
      context.fillStyle = "#ffffff";
      context.fillRect(0, 0, OUTPUT_WIDTH, OUTPUT_HEIGHT);
      if (result.output.width > result.output.height) {
        context.save();
        context.translate(OUTPUT_WIDTH, 0);
        context.rotate(Math.PI / 2);
        context.drawImage(result.output, 0, 0, OUTPUT_HEIGHT, OUTPUT_WIDTH);
        context.restore();
      } else {
        context.drawImage(result.output, 0, 0, OUTPUT_WIDTH, OUTPUT_HEIGHT);
      }
      const file = await canvasToFile(normalized, `cardora-${side}-${Date.now()}.jpg`);
      stopCamera();
      onCapture(file);
    } catch (cropError) {
      setError(cropError.message || "No pudimos recortar la carta.");
      setIsProcessing(false);
    }
  }, [onCapture, side, stopCamera]);

  useEffect(() => {
    if (stage !== "editor" || !editorHostRef.current || !sourceCanvasRef.current) return undefined;
    editorRef.current?.destroy();
    let editor;
    try {
      editor = createCornerEditor({
        container: editorHostRef.current,
        image: sourceCanvasRef.current,
        corners: detectedCorners || guideCorners(sourceCanvasRef.current),
        magnifier: { enabled: true, zoom: 2, size: 96 },
        nudges: { enabled: false },
        toolbar: { enabled: false },
        theme: {
          accent: "#e6c555",
          edgeColor: cropQuality === "auto" ? "#55e0b8" : "#f0d76a",
          surface: "#101512",
          surfaceColor: "#ffffff",
          handleSize: 22,
          handleHit: 48,
          radius: "7px"
        }
      });
    } catch (editorError) {
      setError(editorError.message || "No pudimos abrir el editor de recorte.");
      return undefined;
    }
    editorRef.current = editor;
    return () => {
      editor.destroy();
      if (editorRef.current === editor) editorRef.current = null;
    };
  }, [cropQuality, detectedCorners, stage]);

  const repeatCapture = () => {
    editorRef.current?.destroy();
    editorRef.current = null;
    sourceCanvasRef.current = null;
    setDetectedCorners(null);
    setCropQuality(null);
    setError("");
    setStage("camera");
    setStatus("Alinea la carta completa y toma otra foto.");
  };

  const confirmCrop = () => {
    const corners = editorRef.current?.getCorners?.();
    if (!corners) {
      setError("No pudimos leer el recorte. Repite la foto.");
      return;
    }
    finishCrop(corners);
  };

  const capture = async () => {
    const video = videoRef.current;
    if (!video?.videoWidth || isProcessing) return;
    setIsProcessing(true);
    setError("");
    setStage("analyzing");
    setStatus("Detectando bordes de la carta...");
    try {
      const source = captureVideoFrame(video);
      sourceCanvasRef.current = source;
      await new Promise(resolve => setTimeout(resolve, 30));
      const result = await scanDocument(source, {
        mode: "detect",
        maxProcessingDimension: 980,
        dilationKernelSize: 3,
        minArea: 3000,
        enableDetectionCascade: true,
        maxDocumentAspectRatio: 1.8,
        minDocumentCoverageRatio: 0.16,
        minDocumentFillRatio: 0.18,
        minRightAngleScore: 0.48,
        minOppositeSideConsistency: 0.42,
        minCascadeTriggerConfidence: 0.78
      });
      const isReliable = reliableDetection(result, source);
      setDetectedCorners(isReliable ? result.corners : guideCorners(source));
      setCropQuality(isReliable ? "auto" : "manual");
      setStatus(isReliable
        ? "Recorte sugerido. Revisa las cuatro esquinas antes de continuar."
        : "No hubo una deteccion fiable. Ajusta las cuatro esquinas manualmente.");
      setStage("editor");
    } catch (scanError) {
      const source = sourceCanvasRef.current;
      setDetectedCorners(source ? guideCorners(source) : null);
      setCropQuality("manual");
      setStatus("No hubo una deteccion fiable. Ajusta las cuatro esquinas manualmente.");
      setStage("editor");
    } finally {
      setIsProcessing(false);
    }
  };

  const close = () => {
    stopCamera();
    onCancel();
  };

  return (
    <div className="live-card-scanner scanic-card-scanner">
      {stage === "camera" && (
        <div className="live-card-viewport scanic-camera-stage">
          <video ref={videoRef} playsInline muted autoPlay className="live-card-video" />
          <svg viewBox="0 0 360 480" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
            <rect x="32" y="34" width="296" height="412" rx="14" />
          </svg>
          <span className="live-card-side">{side === "back" ? "Reverso" : "Frente"}</span>
        </div>
      )}
      {stage === "analyzing" && (
        <div className="scanic-analyzing">
          <Spinner animation="border" />
          <strong>Detectando carta...</strong>
          <span>Esto ocurre en tu dispositivo.</span>
        </div>
      )}
      {stage === "editor" && <div ref={editorHostRef} className="scanic-editor-host" />}

      <div className={`live-card-status ${cropQuality ? `is-${cropQuality}` : ""} ${error ? "is-error" : ""}`}>
        <i className={`bi ${stage === "editor" ? "bi-crop" : "bi-bounding-box-circles"}`} />
        <span>{error || status}</span>
      </div>
      <div className="live-card-actions">
        <Button variant="outline-light" onClick={stage === "editor" ? repeatCapture : close}>
          {stage === "editor" ? "Repetir foto" : side === "back" ? "Omitir reverso" : "Cancelar"}
        </Button>
        {stage === "camera" && (
          <Button className="btn-cardora" disabled={Boolean(error) || isProcessing} onClick={capture}>
            <i className="bi bi-camera-fill me-2" />Capturar carta
          </Button>
        )}
        {stage === "editor" && (
          <Button className="btn-cardora" disabled={isProcessing || Boolean(error)} onClick={confirmCrop}>
            {isProcessing ? <Spinner animation="border" size="sm" className="me-2" /> : <i className="bi bi-crop me-2" />}
            Usar recorte
          </Button>
        )}
      </div>
    </div>
  );
}

export default LiveCardScanner;
